import { typedArrayToBuffer } from '@iotize/common/byte-converter';
import { KaitaiStream, UnexpectedDataError, } from './kaitai-stream';
/**
 * left shift operator (<<) does not work when number is on more than 32 bits (ie: (1 << 32) >>> 0 === 1)
 * To left shift when numbers are on +32 bits, use this function
 * @param number
 * @param shift
 * @returns
 */
function shiftLeft(val, shift) {
    return val * Math.pow(2, shift);
}
/**
 * KaitaiStreamReader is an implementation of Kaitai Struct API for JavaScript.
 *
 * Some functions are based on KaitaiStream.js - https://github.com/kaitai-io/kaitai_struct_javascript_runtime/blob/master/KaitaiStream.js that comes with the following license
 *
 * @license Copyright 2012-2016 Ilmari Heikkinen Copyright 2016-2020 Kaitai Project licensed under Apache License, Version 2.0 (the "License");
 *
 * Implementation has been translated to Typescript and modified to fit our needs
 */
export class KaitaiStreamReader extends KaitaiStream {
    static fromArray(array) {
        const buffer = typedArrayToBuffer(array);
        const stream = new KaitaiStreamReader(buffer);
        return stream;
    }
    static create(packet) {
        return packet instanceof Uint8Array
            ? KaitaiStreamReader.fromArray(packet)
            : packet;
    }
    /**
     * @param arrayBuffer ArrayBuffer to read from.
     * @param byteOffset Offset from arrayBuffer beginning for the KaitaiStream.
     */
    constructor(arrayBuffer, byteOffset = 0, isBigEndian = true) {
        super(arrayBuffer, byteOffset, isBigEndian);
    }
    // ========================================================================
    // Integer numbers
    // ========================================================================
    // ------------------------------------------------------------------------
    // Signed
    // ------------------------------------------------------------------------
    readS(size) {
        return this.read('s', size * 8);
    }
    readSigned(size) {
        return this.read('s', size * 8);
    }
    read(type, bitSize, isLittleEndian = this.isLittleEndian) {
        if (type !== 'b') {
            bitSize /= 8;
        }
        const methodType = `read${type.toUpperCase()}${type !== 'b' ? bitSize : ''}${bitSize > 1 && type !== 'b' ? (isLittleEndian ? 'le' : 'be') : ''}`;
        if (!(methodType in this)) {
            throw new Error(`Invalid read type: type=${type}, size=${bitSize}, le=${isLittleEndian}. Method name: ${methodType}`);
        }
        const val = this[methodType](bitSize);
        // console.debug(`\t-Value red ${val}`)
        return val;
    }
    /**
        Reads an 8-bit signed int from the stream.
        @return {number} The read number.
       */
    readS1() {
        const v = this._dataView.getInt8(this.pos);
        this.pos += 1;
        return v;
    }
    // ........................................................................
    // Big-endian
    // ........................................................................
    /**
        Reads a 16-bit big-endian signed int from the stream.
        @return {number} The read number.
       */
    readS2be() {
        const v = this._dataView.getInt16(this.pos);
        this.pos += 2;
        return v;
    }
    /**
        Reads a 32-bit big-endian signed int from the stream.
        @return {number} The read number.
       */
    readS4be() {
        const v = this._dataView.getInt32(this.pos);
        this.pos += 4;
        return v;
    }
    /**
        Reads a 64-bit big-endian unsigned int from the stream. Note that
        JavaScript does not support 64-bit integers natively, so it will
        automatically upgrade internal representation to use IEEE 754
        double precision float.
        @return {number} The read number.
       */
    readS8be() {
        const v1 = this.readU4be();
        const v2 = this.readU4be();
        if ((v1 & 0x80000000) !== 0) {
            // negative number
            return -(0x100000000 * (v1 ^ 0xffffffff) + (v2 ^ 0xffffffff)) - 1;
        }
        else {
            return 0x100000000 * v1 + v2;
        }
    }
    // ........................................................................
    // Little-endian
    // ........................................................................
    /**
        Reads a 16-bit little-endian signed int from the stream.
        @return {number} The read number.
       */
    readS2le() {
        const v = this._dataView.getInt16(this.pos, true);
        this.pos += 2;
        return v;
    }
    /**
        Reads a 32-bit little-endian signed int from the stream.
        @return {number} The read number.
       */
    readS4le() {
        const v = this._dataView.getInt32(this.pos, true);
        this.pos += 4;
        return v;
    }
    /**
        Reads a 64-bit little-endian unsigned int from the stream. Note that
        JavaScript does not support 64-bit integers natively, so it will
        automatically upgrade internal representation to use IEEE 754
        double precision float.
        @return {number} The read number.
       */
    readS8le() {
        const v1 = this.readU4le();
        const v2 = this.readU4le();
        if ((v2 & 0x80000000) !== 0) {
            // negative number
            return -(0x100000000 * (v2 ^ 0xffffffff) + (v1 ^ 0xffffffff)) - 1;
        }
        else {
            return 0x100000000 * v2 + v1;
        }
    }
    // ------------------------------------------------------------------------
    // Unsigned
    // ------------------------------------------------------------------------
    /**
     * Read unsigned
     * @param size
     */
    readU(size) {
        return this.read('u', size * 8);
    }
    readUnsigned(size) {
        return this.read('u', size * 8);
    }
    /**
        Reads an 8-bit unsigned int from the stream.
        @return {number} The read number.
       */
    readU1() {
        const v = this._dataView.getUint8(this.pos);
        this.pos += 1;
        return v;
    }
    // ........................................................................
    // Big-endian
    // ........................................................................
    /**
        Reads a 16-bit big-endian unsigned int from the stream.
        @return {number} The read number.
       */
    readU2be() {
        const v = this._dataView.getUint16(this.pos);
        this.pos += 2;
        return v;
    }
    /**
        Reads a 32-bit big-endian unsigned int from the stream.
        @return {number} The read number.
       */
    readU4be() {
        const v = this._dataView.getUint32(this.pos);
        this.pos += 4;
        return v;
    }
    /**
        Reads a 64-bit big-endian unsigned int from the stream. Note that
        JavaScript does not support 64-bit integers natively, so it will
        automatically upgrade internal representation to use IEEE 754
        double precision float.
        @return {number} The read number.
       */
    readU8be() {
        const v1 = this.readU4be();
        const v2 = this.readU4be();
        return 0x100000000 * v1 + v2;
    }
    // ........................................................................
    // Little-endian
    // ........................................................................
    /**
        Reads a 16-bit little-endian unsigned int from the stream.
        @return {number} The read number.
       */
    readU2le() {
        const v = this._dataView.getUint16(this.pos, true);
        this.pos += 2;
        return v;
    }
    /**
        Reads a 32-bit little-endian unsigned int from the stream.
        @return {number} The read number.
       */
    readU4le() {
        const v = this._dataView.getUint32(this.pos, true);
        this.pos += 4;
        return v;
    }
    /**
        Reads a 64-bit little-endian unsigned int from the stream. Note that
        JavaScript does not support 64-bit integers natively, so it will
        automatically upgrade internal representation to use IEEE 754
        double precision float.
        @return {number} The read number.
       */
    readU8le() {
        const v1 = this.readU4le();
        const v2 = this.readU4le();
        return 0x100000000 * v2 + v1;
    }
    // ========================================================================
    // Floating point numbers
    // ========================================================================
    // ------------------------------------------------------------------------
    // Big endian
    // ------------------------------------------------------------------------
    readF(size) {
        return this.read('f', size * 8);
    }
    readFloat(size) {
        return this.read('f', size * 8);
    }
    readF4be() {
        const v = this._dataView.getFloat32(this.pos);
        this.pos += 4;
        return v;
    }
    readF8be() {
        const v = this._dataView.getFloat64(this.pos);
        this.pos += 8;
        return v;
    }
    // ------------------------------------------------------------------------
    // Little endian
    // ------------------------------------------------------------------------
    readF4le() {
        const v = this._dataView.getFloat32(this.pos, true);
        this.pos += 4;
        return v;
    }
    readF8le() {
        const v = this._dataView.getFloat64(this.pos, true);
        this.pos += 8;
        return v;
    }
    // ------------------------------------------------------------------------
    // Unaligned bit values
    // ------------------------------------------------------------------------
    readBitsBoolean(n) {
        return this.readBitsInt(n) !== 0;
    }
    readB(n) {
        return this.readBitsInt(n);
    }
    readBits(n) {
        return this.readBitsInt(n);
    }
    readBoolean(n) {
        return this.readBitsBoolean(n);
    }
    /**
     * Read n bits as unsigned integer
     * @param n
     * @returns
     */
    readBitsInt(n) {
        if (n > 52)
            throw new Error(`readBitsInt: the maximum supported bit length is 52 (tried to read ${n} bits)`);
        let res = 0;
        let remaining = n;
        while (remaining > 0) {
            const bitsToRead = Math.min(this._bitsLeft, remaining);
            const newBitLeft = this._bitsLeft - bitsToRead;
            const currentBits = this._getBitsFromCurrentPosition();
            let bits = currentBits & ((1 << this._bitsLeft) - 1);
            bits = bits >> newBitLeft;
            res = shiftLeft(res, bitsToRead) + bits;
            remaining -= bitsToRead;
            if (remaining > 0) {
                this.pos++;
            }
            else {
                this._decreaseBitsLeft(bitsToRead);
            }
        }
        return res;
    }
    // ========================================================================
    // Byte arrays
    // ========================================================================
    /**
     *
     * @param len number of bytes(s) to read. If value is < 0, read until up to buffer position 'size - |len|'
     */
    readBytes(len) {
        if (len === undefined) {
            return this.mapUint8Array(this.size - this.pos);
        }
        if (len < 0) {
            len = this.byteLeft + len;
        }
        // console.debug('readBytes', len);
        return this.mapUint8Array(len);
    }
    /**
     * Read all remaining bytes in buffer
     */
    readBytesFull() {
        return this.mapUint8Array(this.size - this.pos);
    }
    /**
     * Read bytes until terminator value is found
     * @throws if eosError is set to true and terminator is not found
     *
     * @param terminator value to search for
     * @param include true to include terminator to result
     * @param consume true to consume terminator
     * @param eosError true to throw an error if we reach end of stream
     */
    readBytesTerm(terminator, { include, consume, eosError, } = {}) {
        const blen = this.size - this.pos;
        const u8 = new Uint8Array(this._buffer, this._byteOffset + this.pos);
        let i;
        for (i = 0; i < blen && u8[i] !== terminator; i++)
            ; // find first zero byte
        if (i === blen) {
            // we've read all the buffer and haven't found the terminator
            if (eosError) {
                throw new Error('End of stream reached, but no terminator ' + terminator + ' found');
            }
            else {
                return this.mapUint8Array(i);
            }
        }
        else {
            let arr;
            if (include) {
                arr = this.mapUint8Array(i + 1);
            }
            else {
                arr = this.mapUint8Array(i);
            }
            if (consume) {
                this.pos += 1;
            }
            return arr;
        }
    }
    /**
     * Ensure that the next stream bytes are the one expected
     * @param expected expected content to check
     * @throws UnexpectedDataError is not the expected bytes
     */
    ensureFixedContents(expected) {
        const actual = this.readBytes(expected.length);
        if (actual.length !== expected.length) {
            throw new UnexpectedDataError(expected, actual);
        }
        const actLen = actual.length;
        for (let i = 0; i < actLen; i++) {
            if (actual[i] !== expected[i]) {
                throw new UnexpectedDataError(expected, actual);
            }
        }
        return actual;
    }
}
